import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { css, cx } from "@strust/ui/css";
import { menu, pagination, stepper, tabs as tabsRecipe } from "@strust/ui/recipes";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from "../icons/icons.tsx";

export interface TabItem {
  readonly id: string;
  readonly label: ReactNode;
  /** Optional count shown in a pill next to the label (already formatted). */
  readonly count?: string;
  readonly panel: ReactNode;
}

export interface TabsProps {
  readonly tabs: readonly TabItem[];
  /** Controlled selection (the app keeps it in the URL). */
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  /** Accessible name of the tab list (from i18n). */
  readonly label: string;
  readonly className?: string;
}

export interface TabListProps {
  readonly tabs: readonly Pick<TabItem, "id" | "label" | "count">[];
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  /** Accessible name of the tab list (from i18n). */
  readonly label: string;
  /** Prefix of the tab ids: a tab is `${idBase}-tab-${id}` (what its panel is labelled by). */
  readonly idBase: string;
  /** Id of the panel a tab controls (default: `${idBase}-panel-${id}`). */
  readonly panelId?: (id: string) => string;
  readonly className?: string;
}

/** The id of a tab of a `TabList` (the `aria-labelledby` of its panel). */
export function tabId(idBase: string, id: string): string {
  return `${idBase}-tab-${id}`;
}

/**
 * The tab buttons alone (WAI-ARIA tabs, automatic activation, arrow/Home/End keys), for a layout
 * that puts them in a card header and renders the panel elsewhere (a table card). The caller
 * renders the `role="tabpanel"` labelled by `tabId(idBase, selectedId)`. `Tabs` is the tab list
 * plus its panel.
 */
export function TabList({
  tabs,
  selectedId,
  onSelect,
  label,
  idBase,
  panelId,
  className,
}: TabListProps) {
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const selected = tabs.find((tab) => tab.id === selectedId) ?? tabs[0];
  const s = tabsRecipe();

  const move = (event: KeyboardEvent, index: number) => {
    const last = tabs.length - 1;
    const target =
      event.key === "ArrowRight"
        ? index === last
          ? 0
          : index + 1
        : event.key === "ArrowLeft"
          ? index === 0
            ? last
            : index - 1
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : null;
    if (target === null) return;
    event.preventDefault();
    const next = tabs[target];
    if (!next) return;
    onSelect(next.id);
    refs.current.get(next.id)?.focus();
  };

  return (
    <div role="tablist" aria-label={label} className={cx(s.list, className)}>
      {tabs.map((tab, index) => {
        const isSelected = tab.id === selected?.id;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              if (node) refs.current.set(tab.id, node);
              else refs.current.delete(tab.id);
            }}
            type="button"
            role="tab"
            id={tabId(idBase, tab.id)}
            aria-selected={isSelected}
            aria-controls={panelId ? panelId(tab.id) : `${idBase}-panel-${tab.id}`}
            tabIndex={isSelected ? 0 : -1}
            className={s.trigger}
            onClick={() => {
              onSelect(tab.id);
            }}
            onKeyDown={(event) => {
              move(event, index);
            }}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className={s.count} data-part="count">
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** WAI-ARIA tabs with automatic activation and arrow/Home/End keyboard support. */
export function Tabs({ tabs, selectedId, onSelect, label, className }: TabsProps) {
  const base = useId();
  const selected = tabs.find((tab) => tab.id === selectedId) ?? tabs[0];
  const s = tabsRecipe();

  return (
    <div className={cx(s.root, className)}>
      <TabList
        tabs={tabs}
        selectedId={selectedId}
        onSelect={onSelect}
        label={label}
        idBase={base}
      />
      {selected ? (
        <div
          role="tabpanel"
          id={`${base}-panel-${selected.id}`}
          aria-labelledby={tabId(base, selected.id)}
          tabIndex={0}
          className={s.panel}
        >
          {selected.panel}
        </div>
      ) : null}
    </div>
  );
}

export interface PaginationProps {
  /** Accessible name of the navigation (e.g. the table caption). */
  readonly label: string;
  /** 1-based. */
  readonly page: number;
  readonly pages: number;
  readonly onPageChange: (page: number) => void;
  readonly previousLabel: string;
  readonly nextLabel: string;
  /** Already formatted, e.g. "Page 2 of 5" or "1–20 of 1,284" (announced politely). */
  readonly status: string;
  /** Accessible name of a numbered page button, e.g. (n) => "Page 3". Omit to hide page numbers. */
  readonly pageLabel?: (page: number) => string;
}

function pageWindow(page: number, pages: number): readonly (number | "gap")[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const set = new Set([1, 2, page - 1, page, page + 1, pages]);
  const sorted = [...set].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  return sorted.flatMap((p, i) => {
    const prev = sorted[i - 1];
    return prev !== undefined && p - prev > 1 ? (["gap", p] as const) : ([p] as const);
  });
}

/** Server-side pagination: status text, previous/next chevrons and optional page numbers. */
export function Pagination({
  label,
  page,
  pages,
  onPageChange,
  previousLabel,
  nextLabel,
  status,
  pageLabel,
}: PaginationProps) {
  const s = pagination();
  return (
    <nav className={s.root} aria-label={label}>
      <span className={s.range} aria-live="polite">
        {status}
      </span>
      <ul className={s.pages}>
        <li>
          <button
            type="button"
            className={s.page}
            aria-label={previousLabel}
            title={previousLabel}
            disabled={page <= 1}
            onClick={() => {
              onPageChange(page - 1);
            }}
          >
            <ChevronLeftIcon aria-hidden="true" />
          </button>
        </li>
        {pageLabel
          ? pageWindow(page, pages).map((entry, index) =>
              entry === "gap" ? (
                <li key={`gap-${String(index)}`} aria-hidden="true" className={s.ellipsis}>
                  …
                </li>
              ) : (
                <li key={entry}>
                  <button
                    type="button"
                    className={s.page}
                    aria-label={pageLabel(entry)}
                    aria-current={entry === page ? "page" : undefined}
                    onClick={() => {
                      onPageChange(entry);
                    }}
                  >
                    {entry}
                  </button>
                </li>
              ),
            )
          : null}
        <li>
          <button
            type="button"
            className={s.page}
            aria-label={nextLabel}
            title={nextLabel}
            disabled={page >= pages}
            onClick={() => {
              onPageChange(page + 1);
            }}
          >
            <ChevronRightIcon aria-hidden="true" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

export interface StepperStep {
  readonly id: string;
  readonly label: string;
  readonly status: "done" | "current" | "upcoming";
}

/** Progress through a multi-step flow (e.g. customer → tenant → invite → accepted). */
export function Stepper({
  label,
  steps,
  doneLabel,
  layout = "inline",
}: {
  readonly label: string;
  readonly steps: readonly StepperStep[];
  /** Visually hidden suffix for completed steps, e.g. "completed". */
  readonly doneLabel: string;
  /** `bar`: full width on the panel surface, the connecting lines stretch between the steps. */
  readonly layout?: "inline" | "bar";
}) {
  return (
    <ol className={stepper({ layout }).root} aria-label={label}>
      {steps.map((step, index) => {
        const s = stepper({ status: step.status, layout });
        return (
          <li
            key={step.id}
            className={s.step}
            aria-current={step.status === "current" ? "step" : undefined}
          >
            {index > 0 ? (
              <span
                aria-hidden="true"
                className={
                  stepper({
                    status: steps[index - 1]?.status === "done" ? "done" : "upcoming",
                    layout,
                  }).link
                }
              />
            ) : null}
            <span aria-hidden="true" className={s.node}>
              {step.status === "done" ? <CheckIcon /> : index + 1}
            </span>
            <span className={s.label}>
              {step.label}
              {step.status === "done" ? (
                <span className={css({ srOnly: true })}>{` (${doneLabel})`}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export interface MenuButtonProps {
  /** Accessible name of the trigger (from i18n). */
  readonly label: string;
  /** Visible content of the trigger; defaults to the label. */
  readonly trigger?: ReactNode;
  /** Class of the trigger (default: a square brushed-steel icon button). */
  readonly triggerClassName?: string;
  readonly children: ReactNode;
  readonly align?: "start" | "end";
  readonly className?: string;
  /** Closes the panel (focus back on the trigger) when a link or button inside it is activated. */
  readonly closeOnSelect?: boolean;
  /**
   * Opens the panel in the browser's top layer, placed under the trigger, so a scroll frame or
   * a card (a table's row menu) never clips it. Without the Popover API it stays inline.
   */
  readonly topLayer?: boolean;
}

/**
 * Disclosure popover (apps, user, notifications, row actions): the trigger toggles a panel,
 * Escape and an outside click close it and focus returns to the trigger. The panel holds
 * ordinary links, buttons and text (styled with `menuItemClass`), so no menu-role arrow
 * navigation is needed.
 */
export function MenuButton({
  label,
  trigger,
  triggerClassName,
  children,
  align = "end",
  className,
  closeOnSelect = false,
  topLayer = false,
}: MenuButtonProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const s = menu({ align });

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={root} className={cx(s.root, className)}>
      <button
        ref={button}
        type="button"
        className={triggerClassName ?? MENU_TRIGGER}
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {trigger ?? label}
      </button>
      {open ? (
        <MenuPanel
          id={panelId}
          className={s.panel}
          label={label}
          anchor={button}
          align={align}
          topLayer={topLayer}
          onClick={(event) => {
            if (
              closeOnSelect &&
              event.target instanceof Element &&
              event.target.closest("a, button")
            ) {
              setOpen(false);
              button.current?.focus();
            }
          }}
        >
          {children}
        </MenuPanel>
      ) : null}
    </div>
  );
}

/** Space between the trigger and its top-layer panel, and between the panel and the viewport. */
const GAP = 6;
const EDGE = 8;

/** `value` within [min, max]; `min` wins when the range is empty (a panel wider than the screen). */
const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

const SUPPORTS_POPOVER =
  typeof HTMLElement !== "undefined" && "showPopover" in HTMLElement.prototype;

/** The panel of a `MenuButton`: inline, or in the top layer under its trigger. */
function MenuPanel({
  id,
  className,
  label,
  anchor,
  align,
  topLayer,
  onClick,
  children,
}: {
  readonly id: string;
  readonly className: string | undefined;
  readonly label: string;
  readonly anchor: { readonly current: HTMLButtonElement | null };
  readonly align: "start" | "end";
  readonly topLayer: boolean;
  readonly onClick: (event: ReactMouseEvent<HTMLDivElement>) => void;
  readonly children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const layered = topLayer && SUPPORTS_POPOVER;

  // Placed with CSSOM (never an inline style attribute: strict CSP), kept on resize and scroll.
  // A fixed panel cannot be scrolled into view, so it must always fit the viewport: under the
  // trigger when there is room, above it otherwise (a row near the bottom of the page), and
  // clamped, scrolling inside itself, when neither side fits.
  useLayoutEffect(() => {
    const element = panel.current;
    if (!layered || !element) return;
    element.showPopover();
    const place = () => {
      const trigger = anchor.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      element.style.position = "fixed";
      element.style.inset = "auto";
      element.style.margin = "0";
      element.style.maxHeight = `${String(Math.max(0, window.innerHeight - 2 * EDGE))}px`;
      element.style.overflowY = "auto";
      const width = element.offsetWidth;
      const height = element.offsetHeight;
      const left = align === "end" ? rect.right - width : rect.left;
      element.style.left = `${String(clamp(left, EDGE, window.innerWidth - width - EDGE))}px`;
      const below = rect.bottom + GAP;
      const above = rect.top - GAP - height;
      const fitsBelow = below + height <= window.innerHeight - EDGE;
      const top = fitsBelow || above < EDGE ? below : above;
      element.style.top = `${String(clamp(top, EDGE, window.innerHeight - height - EDGE))}px`;
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      if (element.matches(":popover-open")) element.hidePopover();
    };
  }, [layered, anchor, align]);

  return (
    <div
      ref={panel}
      id={id}
      className={className}
      role="group"
      aria-label={label}
      {...(layered ? { popover: "manual" } : {})}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

const MENU_TRIGGER = css({
  display: "inline-grid",
  placeItems: "center",
  width: "9",
  height: "9",
  p: "0",
  layerStyle: "brushed",
  borderRadius: "sm",
  cursor: "pointer",
  "& svg": { width: "icon.md", height: "icon.md" },
  _hover: { borderColor: "accent.line" },
  _focusVisible: { outline: "none", boxShadow: "focus" },
  "&[aria-expanded=true]": { borderColor: "accent" },
});

/** Class names for content inside a `MenuButton` panel. */
export const menuClasses = {
  item: menu().item,
  separator: menu().separator,
  heading: menu().heading,
} as const;
